import { useCallback, useEffect, useState } from "react";
import { useDebouncedCallback } from "@/lib/useDebouncedCallback";
import {
  Release,
  ReleasePolicyItem,
  useGetSimulator,
  useGetRelease,
  useUpdateRelease,
} from "../../api/simulator";
import { UpdateStatus } from "@/api/quackchat/configuration/useConfigManager";

export function useSimulatorConfiguration(simulatorId?: string) {
  const [status, setStatus] = useState<UpdateStatus>("idle");

  const {
    data: simulator,
    isLoading: loadingSimulator,
    isFetched: simulatorFetched,
    refetch: refetchSimulator,
  } = useGetSimulator(simulatorId);

  const { data: release } = useGetRelease(
    simulator?.draftReleaseId?.toString()
  );

  const { mutateAsync: updateRelease } = useUpdateRelease(release?.id ?? NaN);

  const updateReleasePolicy = useCallback(
    async (releasePolicy: ReleasePolicyItem[]) => {
      if (simulator == null || release == null) {
        return;
      }

      setStatus("saving");
      const res = await updateRelease({
        id: release.id,
        simulatorId: simulator.id,
        status: release.status,
        releasePolicy: JSON.stringify(releasePolicy),
      });

      if (res != null) {
        await refetchSimulator();
      }

      setStatus("saved");
      return res;
    },
    [release, simulator, updateRelease, refetchSimulator]
  );

  const debouncedUpdateReleasePolicy = useDebouncedCallback<
    Promise<Release | undefined>,
    [ReleasePolicyItem[]]
  >(updateReleasePolicy, 1000);

  useEffect(() => {
    if (status === "saved") {
      const timer = setTimeout(() => setStatus("idle"), 2000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  return {
    simulator,
    loadingSimulator: loadingSimulator || !simulatorFetched,
    release,
    updateReleasePolicy: debouncedUpdateReleasePolicy,
    status,
  };
}
